import React, { forwardRef, useContext } from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import { I18nextContext, Link } from 'gatsby-plugin-react-i18next'
import { directusFileUrl } from 'src/helpers/directus'

interface IsoBadgeProps {
  className?: string
  align?: 'left' | 'right'
  layout?: 'vertical' | 'horizontal' | 'text-above'
}

const DEV_OVERRIDE_ISO = true
const MOCK_ISO_URL = 'https://mamf-www-directus.moveapp.org/assets/e4c8dddc-f3df-4606-b536-d69d612cefbb'

const formatIsoCaption = (text?: string): React.ReactNode => {
  if (!text) return text
  if (text.includes('\n')) {
    return text
  }
  const match = text.match(/^(.*?\bto\b)\s+(.*)$/i)
  if (match) {
    return (
      <>
        {match[1]}
        <br />
        {match[2]}
      </>
    )
  }
  return text
}

const IsoBadge = forwardRef<HTMLDivElement, IsoBadgeProps>(
  ({ className, align = 'left', layout = 'vertical' }, ref) => {
    const data = useStaticQuery(graphql`
      query {
        directus {
          iso_badge {
            translations {
              languages_code {
                code
              }
              caption
              alt
              image {
                id
              }
            }
          }
        }
      }
    `)

    const { language } = useContext(I18nextContext)
    const translations = data?.directus?.iso_badge?.translations
    const currentTranslation =
      translations?.find(
        (translation: any) => translation?.languages_code?.code === language
      ) || translations?.[0]

    if (!currentTranslation && !DEV_OVERRIDE_ISO) {
      return null
    }

    const defaultCaption =
      language === 'pl' ? 'Certyfikat ISO\n9001 i 27001' : 'Certified to\nISO 9001 & 27001'
    const image = currentTranslation?.image
    const alt = currentTranslation?.alt || 'ISO'
    const caption = currentTranslation?.caption || (DEV_OVERRIDE_ISO ? defaultCaption : '')
    const imageSrc = DEV_OVERRIDE_ISO ? MOCK_ISO_URL : (image?.id ? directusFileUrl(image.id) : '')

    if (!imageSrc && !caption) {
      return null
    }

    const isLeft = align === 'left'

    if (layout === 'text-above') {
      return (
        <div
          ref={ref}
          className={`flex flex-col ${isLeft ? 'items-start' : 'items-end'} gap-extra-small w-fit ${align === 'right' ? 'ml-auto' : ''} group ${className || ''}`}
        >
          {caption ? (
            <div className={`font-palanquin font-bold text-body-large lg:text-body-medium xl:text-body-large leading-tight text-on-surface ${isLeft ? 'text-left' : 'text-right'}`}>
              <Link
                to="/iso"
                className={`block whitespace-pre-line group-hover:text-primary transition-colors ${isLeft ? 'text-left' : 'text-right'}`}
              >
                {formatIsoCaption(caption)}
              </Link>
            </div>
          ) : null}
          {imageSrc ? (
            <Link to="/iso" className="block shrink-0">
              <img
                src={imageSrc}
                alt={alt}
                className="h-[62px] lg:h-[48px] xl:h-[62px] w-auto object-contain dark:brightness-0 dark:invert transition-[filter] duration-300"
              />
            </Link>
          ) : null}
        </div>
      )
    }

    if (layout === 'horizontal') {
      return (
        <div
          ref={ref}
          className={`flex items-center gap-medium lg:gap-3 xl:gap-medium w-fit group ${align === 'right' ? 'ml-auto' : ''} ${className || ''}`}
        >
          {caption ? (
            <div className={`font-palanquin font-bold text-body-large lg:text-body-medium xl:text-body-large leading-tight lg:leading-tight xl:leading-tight text-on-surface ${isLeft ? 'text-left' : 'text-right'}`}>
              <Link
                to="/iso"
                className={`block whitespace-pre-line group-hover:text-primary transition-colors ${isLeft ? 'text-left' : 'text-right'}`}
              >
                {formatIsoCaption(caption)}
              </Link>
            </div>
          ) : null}
          {imageSrc ? (
            <Link to="/iso" className="block shrink-0">
              <img
                src={imageSrc}
                alt={alt}
                className="h-[62px] lg:h-[48px] xl:h-[62px] w-auto object-contain dark:brightness-0 dark:invert transition-[filter] duration-300"
              />
            </Link>
          ) : null}
        </div>
      )
    }

    return (
      <div
        ref={ref}
        className={`flex flex-col items-center justify-center gap-extra-small ${
          isLeft ? 'md:items-start md:mr-auto md:ml-0' : 'md:items-end md:ml-auto md:mr-0'
        } w-fit mx-auto ${className || ''}`}
      >
        {imageSrc ? (
          <Link to="/iso" className="block">
            <img
              src={imageSrc}
              alt={alt}
              className="w-32 h-auto object-contain max-h-[80px] dark:brightness-0 dark:invert transition-[filter] duration-300"
            />
          </Link>
        ) : null}
        {caption ? (
          <div className={`font-palanquin font-medium text-center ${isLeft ? 'md:text-left' : 'md:text-right'} leading-tight text-body-large text-on-surface`}>
            <Link to="/iso" className={`block whitespace-nowrap md:whitespace-pre-line hover:text-primary transition-colors text-center ${isLeft ? 'md:text-left' : 'md:text-right'}`}>{formatIsoCaption(caption)}</Link>
          </div>
        ) : null}
      </div>
    )
  }
)

IsoBadge.defaultProps = {
  className: '',
}

export default IsoBadge
